import React from 'react';
import { Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { IconCheck } from '@tabler/icons-react';
import { IDisputeOffer } from '../interfaces/heavyDutyMachinery/IBookingDispute';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { formatCurrency } from '../helpers/currencyHelper';
import { formatShortDateTime } from '../helpers/dateFormatHelper';
import { TEAL, TEAL_SOFT_BG } from '../theme/creamyTokens';

const { Title, Text } = Typography;

interface DisputeOfferListProps {
  offers: IDisputeOffer[];
  currencyCode: string;
  userId?: string;
}

/**
 * The negotiation, oldest first -- the same versioned-proposals shape as
 * the reserva diferida's own quote history, and for the same reason: a
 * back-and-forth over money is a list of positions, and seeing how the two
 * of them moved is most of what tells you whether a deal is close.
 *
 * Renders nothing at all when nobody has proposed anything yet. An empty
 * "Offers" heading is just a heading.
 */
const DisputeOfferList: React.FC<DisputeOfferListProps> = ({ offers, currencyCode, userId }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();

  if (offers.length === 0) return null;

  const ordered = [...offers].sort((a, b) => a.versionNumber - b.versionNumber);

  return (
    <div
      style={{
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: 16,
        padding: 16,
      }}
    >
      <Title level={5} style={{ margin: '0 0 12px' }}>
        {translate(t, TRANSLATIONS.DISPUTE_OFFERS_TITLE)}
      </Title>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {ordered.map((offer) => {
          const mine = offer.offeredByUserId === userId;
          const accepted = !!offer.acceptedAt;
          return (
            <div
              key={offer.id}
              style={{
                padding: 12,
                borderRadius: 12,
                background: accepted ? TEAL_SOFT_BG : token.colorFillQuaternary,
                border: accepted ? `1px solid ${TEAL}` : `1px solid transparent`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {translate(t, mine ? TRANSLATIONS.DISPUTE_OFFER_BY_ME : TRANSLATIONS.DISPUTE_OFFER_BY_THEM)}
                  {' · '}
                  {translate(t, TRANSLATIONS.DISPUTE_OFFER_VERSION, { version: offer.versionNumber })}
                </Text>
                <Text strong style={{ fontSize: 15, whiteSpace: 'nowrap' }}>
                  {formatCurrency(offer.amount, currencyCode, i18n.language)}
                </Text>
              </div>
              {offer.note && (
                <Text style={{ fontSize: 13, display: 'block', marginTop: 4 }}>{offer.note}</Text>
              )}
              <Text type="secondary" style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                {accepted && <IconCheck size={12} color={TEAL} />}
                {formatShortDateTime(offer.acceptedAt ?? offer.createdAt, i18n.language)}
              </Text>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DisputeOfferList;
